import * as React from "react";
import { useCallback, useState } from "react";
import { Button, Field, Tooltip } from "@/components/ui";
import { CTA, normalizeCTA } from "@/services/bedrock";
import { Controller, useFormContext } from "react-hook-form";
import { LockIcon, UnlockIcon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { CTAWizardDialog, CtaTypeDefinitions, describeCta } from "@/components/CTAWizard";

type CtaFormValues = {
  allowCtaChange?: boolean;
  callToAction: CTA | null;
};

interface Props {
  disabled: boolean;
  playerId?: number;
  isLocked?: boolean;
}

const VideoPageCTA: React.FC<Props> = ({ playerId, disabled, isLocked }) => {
  const { control, watch, setValue } = useFormContext<CtaFormValues>();
  const callToAction = watch("callToAction");

  const [wizardOpen, setWizardOpen] = useState(false);

  const { isPlayer } = useSocialCoachSession();

  const handleRemove = useCallback(() => {
    setValue("callToAction", null, { shouldDirty: true });
  }, [setValue]);

  const handleWizardConfirm = useCallback(
    (newCta: CTA) => {
      setValue("callToAction", normalizeCTA(newCta), { shouldDirty: true });
    },
    [setValue]
  );

  const ctaDefinition = callToAction ? CtaTypeDefinitions[callToAction.type] : null;
  const canEdit = !disabled && !isLocked;

  const lockControl =
    isLocked === undefined ? (
      <Controller
        name="allowCtaChange"
        control={control}
        render={({ field }) => (
          <Tooltip>
            <Tooltip.Trigger
              render={
                <Button
                  type="button"
                  size="icon-sm"
                  variant={field.value ? "outline" : "default"}
                  disabled={disabled}
                  onClick={() => field.onChange(!field.value)}
                >
                  {field.value ? <UnlockIcon /> : <LockIcon />}
                </Button>
              }
            />
            <Tooltip.Content>
              {field.value ? "Lock the Call to Action to prevent changes" : "Unlock to allow Call to Action changes"}
            </Tooltip.Content>
          </Tooltip>
        )}
      />
    ) : isLocked ? (
      <Tooltip>
        <Tooltip.Trigger
          render={
            <Button type="button" size="icon-sm" variant="default" className="cursor-default" disabled={disabled}>
              <LockIcon />
            </Button>
          }
        />
        <Tooltip.Content>The Call to Action fields are locked to prevent changes</Tooltip.Content>
      </Tooltip>
    ) : null;

  return (
    <div className="flex flex-col gap-2">
      <Field
        name="callToAction"
        label={
          <div className="flex items-center gap-2">
            <div>Call to Action</div>
            {lockControl}
          </div>
        }
      >
        {callToAction ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-slate-50 p-3">
            <div className="flex items-center gap-3 min-w-0">
              {ctaDefinition && <div className="text-primary shrink-0">{ctaDefinition.icon}</div>}
              <div className="flex flex-col min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">
                  {callToAction.label || "Untitled Button"}
                </div>
                <div className="text-xs text-muted-foreground truncate">{describeCta(callToAction, isPlayer)}</div>
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-1 shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={() => setWizardOpen(true)}>
                  <PencilIcon className="size-3.5" />
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remove Call to Action"
                  onClick={handleRemove}
                >
                  <Trash2Icon className="size-3.5 text-muted-foreground" />
                </Button>
              </div>
            )}
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="w-full justify-start gap-2 border-dashed text-muted-foreground"
            disabled={!canEdit}
            onClick={() => setWizardOpen(true)}
          >
            <PlusIcon className="size-4" />
            Add Call to Action
          </Button>
        )}
      </Field>

      <CTAWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        initialCta={callToAction}
        playerId={playerId}
        onConfirm={handleWizardConfirm}
      />
    </div>
  );
};
export default VideoPageCTA;
